import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { MapPin, ShieldCheck, Star, Flag, ArrowLeft, User as UserIcon, Crown } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, mediaUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { CountdownTimer } from "@/components/CountdownTimer";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";

export default function ItemDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [item, setItem] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [message, setMessage] = useState("");
  const [reason, setReason] = useState("");
  const [requesting, setRequesting] = useState(false);
  const [reqOpen, setReqOpen] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/listings/${id}`); setItem(data); }
    catch { toast.error("Annuncio non trovato"); navigate("/app"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const doRequest = async () => {
    setRequesting(true);
    try {
      const { data } = await api.post("/requests", { listing_id: id, message });
      toast.success("Richiesta inviata!");
      await refresh();
      setReqOpen(false);
      navigate(`/chat/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setRequesting(false); }
  };

  const doReport = async () => {
    try {
      await api.post("/reports", { target_type: "listing", target_id: id, reason });
      toast.success("Segnalazione inviata. Grazie.");
      setReason("");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (!item) return <div className="space-y-4"><Skeleton className="h-96 rounded-2xl" /><Skeleton className="h-8 w-1/2" /></div>;

  const isOwner = item.owner_id === user?.user_id;
  const owner = item.owner;

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mx-auto">
      <Button variant="ghost" onClick={() => navigate(-1)} className="rounded-full mb-4" data-testid="back-btn"><ArrowLeft className="h-4 w-4 mr-1" /> Indietro</Button>

      <div className="grid lg:grid-cols-2 gap-8">
        <div>
          <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-secondary tactile-shadow">
            {item.images?.length ? (
              <img src={mediaUrl(item.images[activeImg])} alt={item.title} className="h-full w-full object-cover" data-testid="detail-main-img" />
            ) : <div className="h-full w-full flex items-center justify-center text-muted-foreground">Nessuna foto</div>}
          </div>
          {item.images?.length > 1 && (
            <div className="flex gap-2 mt-3">
              {item.images.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`h-16 w-16 rounded-xl overflow-hidden border-2 ${i === activeImg ? "border-primary" : "border-transparent"}`}>
                  <img src={mediaUrl(img)} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="secondary">{item.category}</Badge>
            {item.status === "reserved" && <Badge className="bg-accent text-white">Prenotato</Badge>}
            {item.status === "delivered" && <Badge className="bg-success text-white">Consegnato</Badge>}
            {item.expires_at && item.status === "approved" && <CountdownTimer expiresAt={item.expires_at} size="lg" className="border border-border/60" />}
          </div>
          <h1 className="font-head font-extrabold text-3xl sm:text-4xl mt-3">{item.title}</h1>
          <div className="flex items-center gap-1 mt-2 text-sm text-muted-foreground"><MapPin className="h-4 w-4" /> {item.city}{item.province ? ` (${item.province})` : ""}</div>
          <p className="mt-5 text-foreground/90 whitespace-pre-line">{item.description}</p>

          {owner && (
            <Link to={`/u/${owner.user_id}`} className="mt-6 flex items-center gap-3 rounded-2xl border border-border/60 p-4 hover:bg-secondary transition-colors" data-testid="owner-card">
              <Avatar className="h-11 w-11"><AvatarImage src={mediaUrl(owner.avatar_url)} /><AvatarFallback className="bg-primary/10 text-primary">{owner.name?.slice(0,2).toUpperCase()}</AvatarFallback></Avatar>
              <div className="flex-1">
                <div className="font-semibold flex items-center gap-1">{owner.name}
                  {owner.identity_verified && <ShieldCheck className="h-4 w-4 text-success" />}
                  {owner.plan === "premium" && <Crown className="h-4 w-4 text-[hsl(var(--gold))]" />}
                </div>
                <div className="text-xs text-muted-foreground flex items-center gap-1">
                  <Star className="h-3 w-3 text-[hsl(var(--gold))] fill-[hsl(var(--gold))]" /> {owner.rating_avg || "—"} · {owner.donations_count} donazioni
                </div>
              </div>
            </Link>
          )}

          <div className="mt-6 flex flex-col gap-3">
            {isOwner ? (
              <div className="text-sm text-muted-foreground rounded-xl bg-secondary p-4">Questo è il tuo annuncio. Gestiscilo dalla sezione Richieste.</div>
            ) : !user ? (
              <Button size="lg" className="rounded-full h-12 text-base" onClick={() => navigate("/register")} data-testid="request-item-button">Iscriviti per richiederlo</Button>
            ) : item.status === "approved" ? (
              <Dialog open={reqOpen} onOpenChange={setReqOpen}>
                <DialogTrigger asChild>
                  <Button size="lg" className="rounded-full h-12 text-base" data-testid="request-item-button">Richiedi questo oggetto</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Richiedi "{item.title}"</DialogTitle></DialogHeader>
                  <p className="text-sm text-muted-foreground">Invia un messaggio al donatore. La richiesta usa 1 credito se hai superato il limite gratuito mensile (illimitate con Premium).</p>
                  <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Ciao! Sono interessato, quando posso ritirarlo?" className="rounded-xl" data-testid="request-message" />
                  <DialogFooter>
                    <Button onClick={doRequest} disabled={requesting} className="rounded-full w-full" data-testid="confirm-request-btn">{requesting ? "Invio..." : "Invia richiesta"}</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            ) : (
              <div className="text-sm text-muted-foreground rounded-xl bg-secondary p-4">Questo oggetto non è più disponibile.</div>
            )}

            <Dialog>
              <DialogTrigger asChild>
                <Button variant="ghost" className="rounded-full text-muted-foreground" data-testid="report-btn"><Flag className="h-4 w-4 mr-1" /> Segnala annuncio</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Segnala annuncio</DialogTitle></DialogHeader>
                <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Descrivi il problema..." className="rounded-xl" data-testid="report-reason" />
                <DialogFooter><Button onClick={doReport} disabled={!reason} className="rounded-full" data-testid="confirm-report-btn">Invia segnalazione</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
